// React and Context
import { useEffect, useState, useContext } from 'react'
import { IntakeContext } from '../context/IntakeContext'
import { useBuyoutCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useBuyoutCalculations'

//Material and Icons
import {
  Box,
  Grid,
  Icon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'

// Additional Components
import styled from 'styled-components'
const StyledList = styled.div`
  & .yesIcon {
    color: #4caf50;
    background-color: #dbefdc;
    border-radius: 50%;
  }
  & .noIcon {
    color: #fd5c70;
    background-color: #fedee2;
    border-radius: 50%;
  }
`

const OnboardingInformation = ({
  preHeader,
  header,
  subHeader,
  additionalContent,
  styleAdjustments,
}) => {
  const { intakeState } = useContext(IntakeContext)
  const {
    fullyEligibleRetirementBeforeSept30first,
    fullyEligibleRetirementBeforeSept30second,
    fullyEligibleRetirementBeforeSept30third,
    mraPlus10Sept30,
    earlyRetirementSept30,
    notEligibleSept30,
    deferredRetirementSept30first,
    deferredRetirementSept30second,
    deferredRetirementSept30third,
    VERAfirst,
    VERAsecond,
  } = useBuyoutCalculations()
  const whichThing =
    VERAfirst || VERAsecond
      ? {
          pros: [
            'Get Paid Your Salary Through 9/30/2025',
            'Limited, If Any, Work Responsibilities',
            'Retire with Full Benefits on or before 9/30/2025',
          ],
          cons: [
            'No FERS Supplement until MRA',
            "If you are't looking to retire, having to search for another job",
          ],
        }
      : fullyEligibleRetirementBeforeSept30first ||
        fullyEligibleRetirementBeforeSept30second ||
        fullyEligibleRetirementBeforeSept30third
      ? {
          pros: [
            'Get Paid Your Salary Through 9/30/2025',
            'Limited, If Any, Work Responsibilities',
            'Retire with Full Benefits on or before 9/30/2025',
          ],
          cons: [
            'May miss out on future Voluntary Separation Incentive Payments (VSIP) offered',
            'No guarantee you can reverse your resignation',
            "If you are't looking to retire, having to search for another job",
          ],
        }
      : mraPlus10Sept30
      ? {
          pros: [
            'Get Paid Your Salary Through 9/30/2025',
            'Limited, If Any, Work Responsibilities',
            'Retire with Early or Postponed Retirement Benefits',
          ],
          cons: [
            'Reduced or delayed pension',
            'May miss out on future Voluntary Separation Incentive Payments (VSIP) offered',
            'No guarantee you can reverse your resignation',
          ],
        }
      : deferredRetirementSept30first ||
        deferredRetirementSept30second ||
        deferredRetirementSept30third
      ? {
          pros: [
            'Get Paid Your Salary Through 9/30/2025',
            'Not having to return to the office',
            'Potentially work another job and get "double-pay" until 9/30/2025',
          ],
          cons: [
            'Must wait until 62 to turn on pension',
            'No FEHB, FEGLI, or any other Federal Benefits after 9/30/2025',
            'May miss out on future Voluntary Separation Incentive Payments (VSIP) offered',
            "If you aren't looking to retire, having to search for another job",
          ],
        }
      : {
          pros: [
            'Get Paid Your Salary Through 9/30/2025',
            'Limited, If Any, Work Responsibilities',
            'Avoid heading back into the office',
            'Potentially work another job and get "double-pay" until 9/30/2025',
          ],
          cons: [
            'No FEHB, FEGLI, or any other Federal Benefits after 9/30/2025',
            'May miss out on future Voluntary Separation Incentive Payments (VSIP) offered',
            'No guarantee you can reverse your resignation',
          ],
        }

  return (
    <Box sx={{ ...styleAdjustments }}>
      <Box>
        <Typography variant="subtitle1" fontSize={{ xs: '16px', sm: '20px' }}>
          {preHeader || ''}
        </Typography>
      </Box>
      <Box mb={1}>
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          {header || ''}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ lineHeight: '1.2' }}
        fontSize={{ xs: '16px', sm: '20px' }}
      >
        {subHeader || ''}
      </Typography>
      <Typography
        variant="body1"
        sx={{ lineHeight: '1.2', fontStyle: 'italic' }}
        fontSize="12px"
      >
        **These pros and cons may not apply to those in the Foreign Service**
      </Typography>
      <Box pt={1} pb={3} sx={{ ...styleAdjustments }}>
        <Typography variant="h4" textAlign={'left'}>
          Pros:
        </Typography>
        <StyledList>
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 1, boxShadow: 0, border: '1px solid #ddd' }}
          >
            <Table aria-label="Task Recommendations" size="small">
              <TableBody
                sx={{
                  'tr:last-child td, tr:last-child th': { border: 0 },
                  'tr>td:first-of-type': {
                    padding: '10px 0px 4px 16px',
                    width: '1%',
                  },
                  'tr>td': {
                    fontSize: '18px',
                    padding: '10px 16px',
                  },
                }}
              >
                {whichThing.pros.map((pro, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Icon className="yesIcon" fontSize="medium">
                        <CheckIcon />
                      </Icon>
                    </TableCell>
                    <TableCell align="left">{pro}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledList>
        <Typography variant="h4" pt={3} textAlign={'left'}>
          Cons:
        </Typography>
        <StyledList>
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 1, boxShadow: 0, border: '1px solid #ddd' }}
          >
            <Table aria-label="Task Recommendations" size="small">
              <TableBody
                sx={{
                  'tr:last-child td, tr:last-child th': { border: 0 },
                  'tr>td:first-of-type': {
                    padding: '10px 0px 4px 16px',
                    width: '1%',
                  },
                  'tr>td': {
                    fontSize: '18px',
                    padding: '10px 16px',
                  },
                }}
              >
                {whichThing.cons.map((con, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Icon className="noIcon" fontSize="medium">
                        <ClearIcon />
                      </Icon>
                    </TableCell>
                    <TableCell align="left">{con}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledList>
        {additionalContent}
      </Box>
    </Box>
  )
}

export default OnboardingInformation
